import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiPlay, FiMusic, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { dialogueCategoriesAPI, CategoryDialogue, DialoguePartAudio, DialogueUserFlag, authAPI, subscriptionAPI } from '../services/api';
import DialogueFlagSelector from './DialogueFlagSelector';
import { dialogueCategoriesListPath, parseCategoryQueryId } from '../utils/dialogueCategoriesNav';
import './DialogueTopicDetail.css';
import './DialogueFlagSelector.css';

interface DialogueTopicDetailProps {
  onLogout?: () => void;
  userEmail?: string;
}

const DialogueTopicDetail: React.FC<DialogueTopicDetailProps> = ({ onLogout, userEmail }) => {
  const { dialogueId } = useParams<{ dialogueId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isCollapsed } = useSidebar();
  const [dialogue, setDialogue] = useState<CategoryDialogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [userInfo, setUserInfo] = useState({
    email: userEmail || '',
    organization: 'NAATI',
    first_name: '',
    is_staff: false
  });

  useEffect(() => {
    fetchUserProfile();
    checkSubscription();
  }, [userEmail]);

  useEffect(() => {
    if (dialogueId && hasSubscription !== null) {
      fetchDialogue();
    }
  }, [dialogueId, hasSubscription]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || '',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
        is_staff: profile.is_staff || false
      });
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getSubscriptionStatus();
      const profile = await authAPI.getProfile();
      setHasSubscription(status.has_subscription || profile.is_staff || false);
    } catch {
      setHasSubscription(false);
    }
  };

  const fetchDialogue = async () => {
    if (!dialogueId) return;
    try {
      setLoading(true);
      setError(null);
      const idNum = Number(dialogueId);
      if (isNaN(idNum) || idNum <= 0) {
        throw new Error('Invalid dialogue ID');
      }

      const [dialogueData, completionStatus] = await Promise.all([
        dialogueCategoriesAPI.getDialogue(idNum),
        dialogueCategoriesAPI.checkCompletion(idNum).catch(() => ({ is_completed: false }))
      ]);

      setDialogue(dialogueData);
      setIsCompleted(completionStatus.is_completed);

      if ((dialogueData.audios ?? []).length === 0) {
        setError('This dialogue has no audio files');
      }
    } catch (err: any) {
      console.error('Error fetching dialogue:', err);
      setError(err.message || 'Failed to load dialogue');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPractice = () => {
    if (!dialogue) return;
    const categoryId = parseCategoryQueryId(searchParams.get('category')) ?? dialogue.category;
    navigate(`/material/dialogues/${dialogue.id}${categoryId ? `?category=${categoryId}` : ''}`, {
      state: { categoryDialogue: dialogue, dialogueId: dialogue.id, categoryId }
    });
  };

  const goToDialogueList = () => {
    const categoryId = parseCategoryQueryId(searchParams.get('category')) ?? dialogue?.category ?? null;
    navigate(dialogueCategoriesListPath(categoryId));
  };

  const handleFlagChange = async (flag: DialogueUserFlag) => {
    if (!dialogue) return;
    try {
      const result = await dialogueCategoriesAPI.updateUserProgress(dialogue.id, flag);
      setDialogue((prev) =>
        prev
          ? { ...prev, user_flag: result.user_flag, practice_session_count: result.practice_session_count }
          : prev
      );
    } catch (err: any) {
      console.error('Error updating dialogue flag:', err);
      window.alert(err.message || 'Failed to update flag');
    }
  };

  const sortedAudios = dialogue?.audios
    ? [...dialogue.audios].sort((a: DialoguePartAudio, b: DialoguePartAudio) => a.order - b.order)
    : [];

  if (loading) {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userInfo.email}
            organization={userInfo.organization}
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div className="dialogue-topic-detail-loading">
              {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
              <p>Loading dialogue...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dialogue) {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userInfo.email}
            organization={userInfo.organization}
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div className="dialogue-topic-detail-error">
              {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 48 })}
              <p>{error || 'Dialogue not found'}</p>
              <button onClick={goToDialogueList} className="back-btn">
                {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 18 })}
                Back to dialogues
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dialogue-topic-detail-page">
            <motion.button
              className="back-button"
              onClick={goToDialogueList}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 20 })}
              Back to dialogues
            </motion.button>

            <motion.div
              className="topic-detail-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="topic-detail-header">
                <div className="topic-detail-icon">
                  {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 32 })}
                </div>
                <div className="topic-detail-info">
                  <h1 className="topic-detail-title">{dialogue.name}</h1>
                  {dialogue.category_name && (
                    <span className="topic-detail-category">{dialogue.category_name}</span>
                  )}
                  {isCompleted && (
                    <span className="topic-detail-completed-badge">Practiced</span>
                  )}
                  {(dialogue.practice_session_count ?? 0) > 0 && (
                    <span className="topic-detail-practice-count">
                      Practiced {dialogue.practice_session_count} {(dialogue.practice_session_count ?? 0) === 1 ? 'time' : 'times'}
                    </span>
                  )}
                </div>
                <DialogueFlagSelector
                  value={dialogue.user_flag ?? null}
                  onChange={handleFlagChange}
                />
              </div>

              {dialogue.description && (
                <p className="topic-detail-description">{dialogue.description}</p>
              )}

              <div className="topic-materials-section">
                <h3>Materials ({sortedAudios.length} audios)</h3>
                <ul className="materials-list">
                  {sortedAudios.map((audioLink: DialoguePartAudio, idx: number) => (
                    <li key={audioLink.id} className="material-item">
                      <span className="material-order">{idx + 1}.</span>
                      <span className="material-name">
                        {audioLink.audio_file_data?.file_name || `Audio ${idx + 1}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                className="start-practice-btn"
                onClick={handleStartPractice}
                disabled={sortedAudios.length === 0}
                title={isCompleted ? 'Practice this dialogue again' : 'Start practicing'}
              >
                {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 20 })}
                {isCompleted ? 'Practice Again' : 'Start Practice'}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialogueTopicDetail;
